import { PropsWithChildren, useCallback, useEffect, useRef } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { constants } from '@core';
import { autoplayEnabledAtom, MAX_PROGRESS, playbackSpeedAtom, progressAtom, totalFramesAtom } from '@store';

const SECOND = 1_000;

/**
 * Complete autoplay events. We run a hook here which increments global progress.
 * Without this Autoplay does not work.
 */
export const WithAutoplay = ({ children }: PropsWithChildren) => {
    const autoplayEnabled = useAtomValue(autoplayEnabledAtom);
    const totalFrames = useAtomValue(totalFramesAtom);
    const playbackSpeed = useAtomValue(playbackSpeedAtom);
    const setProgress = useSetAtom(progressAtom);

    const rafId = useRef<number | null>(null);
    const rafLastTime = useRef(performance.now());

    const videoDuration = (totalFrames / constants.CAMERA_FPS) * SECOND;

    const animate = useCallback(
        (now: number) => {
            const deltaTime = now - rafLastTime.current;
            rafLastTime.current = now;
            setProgress((prev) => {
                const next = prev + (deltaTime * MAX_PROGRESS * playbackSpeed) / videoDuration;
                if (next > MAX_PROGRESS) return 0;
                return next;
            });

            rafId.current = requestAnimationFrame(animate);
        },
        [playbackSpeed, setProgress, videoDuration],
    );

    useEffect(() => {
        if (!autoplayEnabled) {
            return;
        }

        // Reset timestamp when starting playback to avoid huge deltaTime jump
        rafLastTime.current = performance.now();
        rafId.current = requestAnimationFrame(animate);

        return () => {
            if (rafId.current !== null) {
                cancelAnimationFrame(rafId.current);
            }
        };
    }, [animate, autoplayEnabled]);

    return children;
};
